/// <reference types="@emotion/react/types/css-prop" />
import {
  LayoutModule,
  ModuleProperties,
  useAnimationStates,
  useShowInstructions,
  useRenderedChildren,
} from '@backstage-components/base';
import {cx, css} from '@emotion/css';
import {Box, type BoxProps} from '@chakra-ui/react';
import {motion, type Variants} from 'framer-motion';
import {useSubscription} from 'observable-hooks';
import {Fragment, FC, useState, useRef, useEffect} from 'react';
import {
  reactName,
  ComponentDefinition,
  SchemaType,
  defaultFieldData,
  animationTypeEnum,
} from './BackgroundVideoDefinition';
import {
  videoBackgroundStyle,
  videoStyle,
  overlayStyle,
  vinetteOverlayStyle,
} from './BackgroundVideo.styled';

export interface IBackgroundVideoProps extends ModuleProperties {}

export type BackgroundVideoComponentDefinition = LayoutModule<
  'BackgroundVideo',
  IBackgroundVideoProps & SchemaType
>;

export const TEST_ID = `${reactName}-test`;

const MotionBox = motion<BoxProps>(Box);

// Define a type that represents all possible values of animationTypeEnum
type AnimationType = (typeof animationTypeEnum)[number];

function getVideoAnimationVariants(
  animationDuration: number | undefined,
  animationType: AnimationType
): Variants {
  const dur = animationDuration ? animationDuration : 1000;
  return {
    hidden: {
      opacity: 0,
      scale: animationType === 'Fade and Scale' ? 1.12 : 1,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: animationType === 'None' ? 0 : dur / 1000,
        ease: 'easeInOut',
      },
    },
  };
}

export const BackgroundVideoComponent: FC<
  BackgroundVideoComponentDefinition
> = (definition) => {
  const {props} = definition;
  const {
    overlayOpacity,
    muted,
    autoplay,
    loop,
    source,
    posterImage,
    showPosterImage,
    overlayType,
    animationStates,
    animationType = defaultFieldData.animationType,
    animationDuration = defaultFieldData.animationDuration,
  } = props;
  const [computedSrc, setComputedSrc] = useState(source.progressive);
  const {slotRenderer: Component = () => <Fragment />} = definition;
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setComputedSrc(source.progressive);
  }, [source.progressive]);

  useEffect(() => {
    const playVideo = async (): Promise<void> => {
      if (videoRef.current) {
        try {
          await videoRef.current.play();
        } catch (error: unknown) {
          console.error('Autoplay failed:', error);
          // Show the video element if there is an error, this is to avoid a blank screen
          // If a poster image is present it will show
          setVideoLoaded(true);
        }
      }
    };

    playVideo();
  }, []);

  const handleVideoLoad = (): void => {
    setVideoLoaded(true);
  };

  const renderedChildren = useRenderedChildren(Component, definition.slots);

  // This is the connection to instructions in the backstage system
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );

  const motionOptions = useAnimationStates(
    observable,
    broadcast,
    animationStates
  );

  // React to animation state requests
  useSubscription(observable, (inst): void => {
    switch (inst.type) {
      case 'BackgroundVideo:play': {
        videoRef.current?.play();
        break;
      }
      case 'BackgroundVideo:pause': {
        videoRef.current?.pause();
        break;
      }
      default:
        break;
    }
  });

  const handleVideoPlay = (): void => {
    broadcast({type: 'BackgroundVideo:on-play', meta: {}});
  };

  const handleVideoEnded = (): void => {
    broadcast({type: 'BackgroundVideo:on-ended', meta: {}});
  };

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  const videoAnimationVariants = getVideoAnimationVariants(
    animationDuration,
    animationType
  );

  return (
    <MotionBox
      id={definition.id}
      data-testid={TEST_ID}
      css={videoBackgroundStyle}
      className={cx(styleClassName, definition.mid)}
      {...motionOptions}
    >
      <motion.video
        initial="hidden"
        animate={videoLoaded ? 'visible' : 'hidden'}
        onLoadedData={handleVideoLoad}
        variants={videoAnimationVariants}
        autoPlay={autoplay}
        muted={muted}
        loop={loop}
        playsInline
        css={videoStyle}
        poster={showPosterImage ? posterImage : ''}
        ref={videoRef}
        onPlay={handleVideoPlay}
        onEnded={handleVideoEnded}
        key={`${computedSrc}-${posterImage}`}
        role="video"
        onError={handleVideoLoad}
      >
        <source src={computedSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </motion.video>
      {overlayType === 'Flat' && (
        <Box
          css={overlayStyle}
          background={`rgba(0, 0, 0, ${overlayOpacity?.opacity ?? 0.25})`}
          className="flat-overlay"
          data-testid="flat-overlay"
        />
      )}
      {overlayType === 'Vignette' && (
        <Box
          className="vignette-overlay"
          data-testid="vignette-overlay"
          opacity={overlayOpacity}
          css={vinetteOverlayStyle}
        />
      )}
      <Box position="relative" zIndex="3">
        {renderedChildren}
      </Box>
    </MotionBox>
  );
};
